import { useMemo } from "react";
import { Box, Typography, Avatar, Tooltip } from "@mui/material";
import ExpandIcon from "../../assets/interaction-detail-page/expand-up-double.svg";
import CollapseIocn from "../../assets/interaction-detail-page/collapse-double.svg";

interface ParticipantBoxProps {
  speakerSegmentsMap: Record<
    string,
    {
      participant?: any;
      segments?: { start: number; end: number; startTime?: string; endTime?: string }[];
      totalDuration?: number | string;
      participantFrom?: string;
    }
  >;
  withoutDurationAll: [string, { participant?: any }][];
  domain: string;
  renderSpeaker: (speaker: string, participant: any, totalDuration: number) => JSX.Element;
  handleSegmentClick?: (start: number) => void;
  meetingDuration?: number;
  theme?: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

const ParticipantBox = ({
  speakerSegmentsMap,
  withoutDurationAll,
  domain,
  renderSpeaker,
  handleSegmentClick,
  meetingDuration = 1,
  isExpanded,
  onToggleExpand,
}: ParticipantBoxProps) => {
  const participantTypes = useMemo(() => {
    const typesSet = new Set<string>();
    Object.values(speakerSegmentsMap || {}).forEach((entry) => {
      if (entry?.participantFrom) {
        typesSet.add(entry.participantFrom.trim().toLowerCase());
      }
    });

    const pinnedOrder = ["account", "client"];
    return Array.from(typesSet).sort((a, b) => {
      const indexA = pinnedOrder.indexOf(a);
      const indexB = pinnedOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
  }, [speakerSegmentsMap]);

  const formatTypeTitle = (type: string) => {
    if (!type) return "Other";
    return type
      .replace(/[-_]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getSectionTitle = (type: string) => {
    const normalized = type?.toLowerCase()?.trim();
    if (normalized === "account") {
      return `${domain} Participants Who Contributed`;
    }
    const formattedType = formatTypeTitle(type);
    return `${formattedType} Participants Who Contributed`;
  };

  const visibleTypes = participantTypes.filter((type) =>
    Object.entries(speakerSegmentsMap).some(
      ([, value]) =>
        (value.participantFrom || "other").trim().toLowerCase() === type &&
        Number(value.totalDuration) > 0
    )
  );
  const hasExpandableContent = visibleTypes.length > 0 || withoutDurationAll.length > 0;

  return (
    <Box
      className="callStats-participantsBox"
      sx={{
        flex: "1 1 20%",
        pr: 2,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {hasExpandableContent && (
      <Box className="callStats-expand-box-container">
        <Box
          className="callStats-expand-box"
          onClick={onToggleExpand}
        >
          <Typography sx={{ fontSize: 12, color: "#6036D6", fontWeight: 500 }}>
            {isExpanded ? "Collapse" : "Expand"}
          </Typography>
          <img
            src={isExpanded ? CollapseIocn : ExpandIcon}
            width={16}
            height={16}
          />
        </Box>
      </Box>
      )}
      {participantTypes.map((type) => {
      const grouped = Object.entries(speakerSegmentsMap).filter(
        ([, value]) => (value.participantFrom || "other").trim().toLowerCase() === type
      );

      const withDuration = grouped.filter(
        ([, { totalDuration }]) => Number(totalDuration) > 0
      );
      if (withDuration.length === 0) return null;

      const allEmpty =
        Object.values(speakerSegmentsMap || {}).length === 0 &&
        (!withoutDurationAll || withoutDurationAll.length === 0);

      if (allEmpty) {
        return (
          <Box
            className="callStats-participantsBox"
            sx={{
              flex: "1 1 40%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontStyle: 'var(--font-family)',
              borderRight: "1px solid #e0e0e0",
              height: "100%",
            }}
          >
            No participants found for this meeting.
          </Box>
        );
      }

      const formatTime = (seconds: number) => {
        const totalSeconds = Math.round(seconds);
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;

        if (mins > 0) {
          return `${mins} min${mins > 1 ? "s" : ""} ${secs} sec${secs !== 1 ? "s" : ""}`;
        }
        return `${secs} sec${secs !== 1 ? "s" : ""}`;
      };

      return (
        <Box key={type} className="callStats-participant-section">
          <Box className="callStats-participant-expand">
          <Typography variant="h3" className="callStats-participantTitle">
            {getSectionTitle(type)}
          </Typography>
        </Box>

          {withDuration.map(([speaker, data]) => {
            const participant = data?.participant ?? { name: speaker, email: "", pictureUrl: "" };
            const segments = data?.segments ?? [];
            const totalDuration = Number(data?.totalDuration) || 0;

            return (
              <Box key={participant.email || speaker} className="callStats-speakerContainer">
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                {renderSpeaker(speaker, participant, totalDuration)}

                {segments.length > 0 && handleSegmentClick && (
                  <Box className="callStats-speakerBarWrapper">
                    {segments.map((seg, idx) => {
                      const left = (seg.start / meetingDuration) * 100;
                      const width = ((seg.end - seg.start) / meetingDuration) * 100;

                      return (
                        <Tooltip key={idx} title={formatTime(seg.start)} arrow>
                          <Box
                            onClick={() => handleSegmentClick(seg.start)}
                            className="callStats-speakerSegment"
                            sx={{
                              left: `${left}%`,
                              width: `${Math.max(width, 0.5)}%`,
                              backgroundColor: "#CEC1F2",
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    })}

    {/* Non-contributors */}
    {withoutDurationAll.length > 0 && (
      <>
        <Typography className="callStats-participantTitle">Other Invitees</Typography>
        <Box className="callStats-inviteeGrid">
          {withoutDurationAll.map(([speaker, { participant }]) => {
            const p = participant ?? { name: speaker, email: "", pictureUrl: "" };
            return (
              <Box key={p.email || speaker} className="callStats-inviteeItem">
                <Tooltip title={p.email || ""} arrow>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={p.pictureUrl}
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: participant ? undefined : "#BDBDBD",
                    }}
                  >
                    {p.name?.[0]?.toUpperCase() || "U"}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontSize: 11, fontFamily: 'var(--font-family)' }}>
                    {p.name || p.email || speaker}
                  </Typography>
                </Box>
              </Tooltip>
              </Box>
            );
          })}
        </Box>
      </>
    )}
    </Box>
  );
};

export default ParticipantBox;
